// Re-export type
export type { BlogPost } from "./blog/types";

// Import all posts
import { BlogPost } from "./blog/types";
import { comoComprimirSinPerderCalidad } from "./blog/como-comprimir-imagenes-sin-perder-calidad";
import { jpgVsPngVsWebp } from "./blog/jpg-vs-png-vs-webp";
import { optimizarImagenesParaWeb } from "./blog/optimizar-imagenes-para-web";
import { comoComprimirParaWeb } from "./blog/como-comprimir-imagenes-para-web";
import { comoComprimirImagenA100kb } from "./blog/como-comprimir-imagen-a-100kb";
import { comoReducirImagenParaWhatsapp } from "./blog/como-reducir-imagen-para-whatsapp";
import { comoReducirImagenParaFormulario } from "./blog/como-reducir-imagen-para-formulario";
import { comprimirImagenSinPerderCalidad } from "./blog/comprimir-imagen-sin-perder-calidad";
import { comprimirImagenA200kb } from "./blog/comprimir-imagen-a-200kb";
import { comoEnviarImagenWhatsappSinPerderCalidad } from "./blog/como-enviar-imagen-whatsapp-sin-perder-calidad";
import { convertirPngAJpg } from "./blog/convertir-png-a-jpg";
import { reducirImagenParaEmail } from "./blog/reducir-imagen-para-email";
import { comprimirImagenA50kb } from "./blog/comprimir-imagen-a-50kb";
import { reducirImagenA300kb } from "./blog/como-reducir-imagen-a-300kb";
import { comprimirImagenA20kb } from "./blog/como-comprimir-imagen-a-20kb";
import { reducirImagenParaDni } from "./blog/como-reducir-imagen-para-dni";
import { comprimirFotoParaCv } from "./blog/como-comprimir-foto-para-cv";
import { comoReducirImagenParaEmail } from "./blog/como-reducir-imagen-para-email";
import { reducirImagenParaInstagram } from "./blog/como-reducir-imagen-para-instagram";
import { mejorarImagenBorrosa } from "./blog/como-mejorar-imagen-borrosa-online";
import { aclararImagenBorrosa } from "./blog/como-aclarar-imagen-borrosa";
import { arreglarFotosBorrosas } from "./blog/arreglar-fotos-borrosas-gratis";
import { arreglarImagenPixelada } from "./blog/como-arreglar-imagen-pixelada";

export const blogPosts: BlogPost[] = [
  arreglarImagenPixelada,
  arreglarFotosBorrosas,
  aclararImagenBorrosa,
  mejorarImagenBorrosa,
  reducirImagenParaInstagram,
  comoReducirImagenParaEmail,
  comprimirFotoParaCv,
  reducirImagenParaDni,
  comprimirImagenA20kb,
  reducirImagenA300kb,
  comprimirImagenA50kb,
  reducirImagenParaEmail,
  convertirPngAJpg,
  comoEnviarImagenWhatsappSinPerderCalidad,
  comprimirImagenA200kb,
  comprimirImagenSinPerderCalidad,
  comoReducirImagenParaFormulario,
  comoComprimirImagenA100kb,
  comoReducirImagenParaWhatsapp,
  comoComprimirSinPerderCalidad,
  jpgVsPngVsWebp,
  optimizarImagenesParaWeb,
  comoComprimirParaWeb,
  {
    slug: "diferencia-entre-jpg-png-webp",
    title: "Diferencia entre JPG, PNG y WebP: ¿Cuál Usar?",
    description: "Entiende las diferencias clave entre los formatos de imagen más populares. Guía práctica para elegir el formato correcto según tu necesidad.",
    date: "2026-02-10",
    readTime: "4 min",
    keywords: "diferencia jpg png, jpg vs png, webp vs jpg, formatos imagen, cuando usar png, cuando usar jpg",
    content: `
## Comparación rápida de formatos

| Característica | JPEG/JPG | PNG | WebP |
|---------------|----------|-----|------|
| Compresión | Con pérdida | Sin pérdida | Ambas |
| Transparencia | ❌ No | ✅ Sí | ✅ Sí |
| Animación | ❌ No | ❌ No | ✅ Sí |
| Tamaño archivo | Pequeño | Grande | Muy pequeño |
| Calidad | Buena | Perfecta | Excelente |
| Soporte navegadores | Universal | Universal | Todos modernos |

## JPEG/JPG: El formato universal para fotos

JPEG (Joint Photographic Experts Group) es el formato más utilizado para fotografías. Usa **compresión con pérdida**, lo que significa que descarta información visual que el ojo humano difícilmente nota.

### Ventajas
- Archivos muy pequeños (ideal para web)
- Soporte universal en todos los dispositivos
- Excelente para fotografías con muchos colores

### Desventajas
- No soporta transparencia
- Cada vez que se guarda, pierde un poco de calidad
- No ideal para texto, logos o gráficos simples

## PNG: Calidad perfecta con transparencia

PNG (Portable Network Graphics) usa **compresión sin pérdida**, manteniendo cada pixel exactamente como el original.

### Ventajas
- Calidad perfecta sin degradación
- Soporta transparencia (canal alfa)
- Ideal para logos, iconos y gráficos

### Desventajas
- Archivos significativamente más grandes que JPEG
- No recomendado para fotografías grandes

## WebP: Lo mejor de ambos mundos

Desarrollado por Google, WebP combina las ventajas de JPEG y PNG con **mejor compresión**.

### Ventajas
- 25-35% más pequeño que JPEG con calidad similar
- Soporta transparencia como PNG
- Soporta animación como GIF
- Compresión con y sin pérdida

### Desventajas
- No compatible con navegadores muy antiguos (IE11)
- Menor soporte en software de edición antiguo

## ¿Cuál elegir?

- **Fotos para web** → WebP (con fallback JPEG)
- **Logos y gráficos** → PNG o SVG
- **Compatibilidad máxima** → JPEG
- **Transparencia necesaria** → PNG o WebP
- **Mejor compresión posible** → WebP

Usa nuestro **[compresor de imágenes](/)** para convertir y optimizar entre formatos automáticamente.
    `
  },
  {
    slug: "optimizar-imagenes-para-seo",
    title: "Cómo Optimizar Imágenes para SEO: Guía Completa 2026",
    description: "Aprende a optimizar las imágenes de tu web para mejorar el posicionamiento en Google. Técnicas de SEO para imágenes que funcionan en 2026.",
    date: "2026-02-05",
    readTime: "6 min",
    keywords: "optimizar imagenes seo, seo imagenes, alt text imagenes, google images seo, posicionamiento imagenes",
    content: `
## ¿Por qué importa el SEO de imágenes?

Google Images representa el **20-25% de todas las búsquedas** en Google. Optimizar tus imágenes no solo mejora la velocidad de tu web, sino que te abre una fuente importante de tráfico orgánico.

## 7 técnicas clave para SEO de imágenes

### 1. Nombres de archivo descriptivos

❌ Malo: \`IMG_20240115_123456.jpg\`
✅ Bueno: \`compresor-imagenes-online-gratis.jpg\`

Usa guiones para separar palabras e incluye tu keyword principal.

### 2. Texto alternativo (alt text) optimizado

El atributo \`alt\` es el factor más importante para SEO de imágenes:

\`\`\`html
<!-- Malo -->
<img alt="imagen" />

<!-- Bueno -->
<img alt="Herramienta online para comprimir imágenes JPG gratis" />
\`\`\`

### 3. Comprimir sin perder calidad

Imágenes pesadas ralentizan tu web y Google penaliza la velocidad. Usa nuestro **[compresor de imágenes](/)** para reducir el tamaño hasta 90%.

### 4. Usar dimensiones correctas

No cargues imágenes de 4000x3000 si se muestran a 800x600. Redimensiona antes de subir.

### 5. Implementar lazy loading

\`\`\`html
<img src="imagen.jpg" loading="lazy" alt="Descripción" />
\`\`\`

### 6. Usar formatos modernos (WebP)

WebP ofrece 25-35% mejor compresión que JPEG. Usa \`<picture>\` para compatibilidad:

\`\`\`html
<picture>
  <source srcset="imagen.webp" type="image/webp" />
  <img src="imagen.jpg" alt="Descripción" />
</picture>
\`\`\`

### 7. Datos estructurados para imágenes

Agrega marcado Schema.org para que Google entienda mejor tus imágenes y las muestre en resultados enriquecidos.

## Checklist rápido

- [ ] Nombres de archivo descriptivos con keywords
- [ ] Alt text relevante y descriptivo
- [ ] Imágenes comprimidas (usar [CompresorJPEG](/))
- [ ] Dimensiones apropiadas para el diseño
- [ ] Lazy loading implementado
- [ ] Formato WebP cuando sea posible
- [ ] Sitemap de imágenes configurado
    `
  },
  {
    slug: "como-comprimir-imagenes-para-instagram",
    title: "Cómo Comprimir Imágenes para Instagram sin Perder Calidad",
    description: "Guía para optimizar tus fotos para Instagram. Tamaños recomendados, formatos ideales y técnicas para que tus publicaciones se vean perfectas.",
    date: "2026-01-28",
    readTime: "4 min",
    keywords: "comprimir imagenes instagram, tamaño fotos instagram, optimizar fotos instagram, calidad instagram, dimensiones instagram",
    content: `
## Tamaños recomendados para Instagram en 2026

| Tipo de publicación | Tamaño | Relación aspecto |
|---|---|---|
| Post cuadrado | 1080 x 1080 px | 1:1 |
| Post vertical | 1080 x 1350 px | 4:5 |
| Post horizontal | 1080 x 566 px | 1.91:1 |
| Story/Reel | 1080 x 1920 px | 9:16 |
| Foto de perfil | 320 x 320 px | 1:1 |

## ¿Por qué comprimir fotos para Instagram?

Instagram **recomprime automáticamente** tus imágenes al subirlas. Si subes una foto sin optimizar, Instagram aplicará una compresión agresiva que puede degradar la calidad notablemente.

La estrategia óptima es:
1. **Redimensionar** al tamaño exacto recomendado
2. **Comprimir con calidad alta** (85-90%) antes de subir
3. Usar **formato JPEG** para fotos y **PNG** para gráficos

## Paso a paso

### 1. Redimensiona tu imagen
Ajusta al tamaño exacto que necesitas (ej: 1080x1350 para posts verticales).

### 2. Comprime con calidad controlada
Usa nuestro **[compresor de imágenes](/)** con calidad entre 85-90%. Esto reduce el tamaño del archivo sin pérdida visible de calidad.

### 3. Sube directamente
Al subir una imagen ya optimizada, Instagram aplicará menos compresión adicional, resultando en mejor calidad final.

## Tips profesionales

- **Evita subir desde capturas de pantalla**: Ya tienen compresión aplicada
- **Usa el modo profesional** de la cámara para obtener la máxima calidad inicial
- **No apliques filtros de Instagram** si ya editaste la foto en otro programa
- **Sube desde el computador** usando Creator Studio para evitar la recompresión del móvil

## Herramienta recomendada

Nuestro **[compresor de imágenes online](/)** te permite ajustar la calidad exacta y redimensionar tus fotos al tamaño perfecto para Instagram, todo desde el navegador.
    `
  },
  {
    slug: "guia-core-web-vitals-imagenes",
    title: "Guía Core Web Vitals: Cómo las Imágenes Afectan tu SEO",
    description: "Entiende cómo las imágenes impactan en Core Web Vitals (LCP, CLS) y aprende a optimizarlas para mejorar tu posicionamiento en Google.",
    date: "2026-01-20",
    readTime: "5 min",
    keywords: "core web vitals imagenes, lcp optimizar, cls imagenes, rendimiento web imagenes, google page speed imagenes",
    content: `
## ¿Qué son los Core Web Vitals?

Son métricas de Google que miden la experiencia real del usuario en tu web:

- **LCP (Largest Contentful Paint)**: Tiempo que tarda en cargar el elemento más grande visible (frecuentemente una imagen)
- **CLS (Cumulative Layout Shift)**: Cambios inesperados en el diseño de la página
- **INP (Interaction to Next Paint)**: Tiempo de respuesta a interacciones del usuario

## Cómo las imágenes afectan cada métrica

### LCP: El impacto más directo

El LCP frecuentemente es una imagen hero o banner. Si esa imagen pesa 2MB, tu LCP será terrible.

**Solución**:
- Comprime la imagen hero a menos de 200KB
- Usa \`<link rel="preload">\` para cargarla prioritariamente
- Considera usar WebP para 25-35% menos peso

### CLS: Layout shifts por imágenes

Cuando una imagen carga sin dimensiones definidas, el contenido "salta", causando CLS alto.

**Solución**:
\`\`\`html
<!-- Siempre define width y height -->
<img src="foto.jpg" width="800" height="600" alt="Descripción" />

<!-- O usa aspect-ratio en CSS -->
<img src="foto.jpg" style="aspect-ratio: 4/3" alt="Descripción" />
\`\`\`

### INP: Imágenes y rendimiento

Imágenes muy pesadas pueden bloquear el hilo principal del navegador durante la decodificación.

**Solución**:
- Usa \`loading="lazy"\` para imágenes fuera del viewport
- Usa \`decoding="async"\` para decodificación no bloqueante

## Checklist de optimización

1. **Comprimir todas las imágenes** con [CompresorJPEG](/)
2. **Definir dimensiones** (width/height) en todas las imágenes
3. **Lazy loading** para imágenes below the fold
4. **Preload** la imagen LCP
5. **WebP** como formato principal
6. **Responsive images** con srcset para diferentes pantallas

## Herramientas de medición

- [Google PageSpeed Insights](https://pagespeed.web.dev/) - Análisis completo
- [Chrome DevTools Lighthouse](https://developers.google.com/web/tools/lighthouse) - Auditoría local
- [Web Vitals Extension](https://chrome.google.com/webstore/detail/web-vitals/) - Monitoreo en tiempo real

## Optimiza tus imágenes ahora

Usa nuestro **[compresor de imágenes](/)** para reducir el peso de todas tus imágenes y mejorar tus Core Web Vitals significativamente.
    `
  }
];
